import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
} from "@/components/primitives";
import {
	Brain,
	TrendingDown,
	TrendingUp,
	Lightbulb,
	Calendar,
	BarChart3,
	AlertCircle,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { AnalysisGenerator } from "@/components/AnalysisGenerator";

interface CheckInAnalysis {
	mainTopics: Array<{
		topic: string;
		frequency: number;
		sentiment: number;
		context?: string;
	}>;
	negativeTopics: Array<{
		topic: string;
		frequency: number;
		sentiment: number;
		impact: string;
		patterns: string;
		trends?: string;
		specifics?: string;
	}>;
	positiveTopics: Array<{
		topic: string;
		frequency: number;
		sentiment: number;
		impact: string;
		patterns: string;
		trends?: string;
		specifics?: string;
	}>;
	reflectionSuggestion: {
		title: string;
		description: string;
		reasoning: string;
		actionableInsights?: string[];
	};
	reflectionSuggestions?: Array<{
		title: string;
		description: string;
		reasoning?: string;
	}>;
}

interface AnalysisData {
	id: string;
	user_id: string;
	analysis_date: string;
	checkin_count: number;
	last_checkin_date: string;
	analysis_data: CheckInAnalysis;
	created_at: string;
	updated_at: string;
}

export interface DailyCheckInAnalysisRef {
	refresh: () => void;
	showPending: () => void;
	getAnalysis: () => CheckInAnalysis | null;
	getState: () => {
		loading: boolean;
		pending: boolean;
		hasAnalysis: boolean;
		lastUpdated: string | null;
	};
}

interface DailyCheckInAnalysisCleanProps {
	activeReflections?: Array<{
		id: string;
		title: string;
		description?: string;
	}>;
	centerHeader?: boolean;
	initialAnalysis?: CheckInAnalysis | null; // Allow passing analysis directly (e.g., when just generated)
}

export const DailyCheckInAnalysisClean = forwardRef<
	DailyCheckInAnalysisRef,
	DailyCheckInAnalysisCleanProps
>(
	(
		{ activeReflections = [], centerHeader = false, initialAnalysis = null },
		ref,
	) => {
		const { user } = useAuth();
		const [analysis, setAnalysis] = useState<CheckInAnalysis | null>(
			initialAnalysis,
		);
		const [loading, setLoading] = useState(true);
		const [pending, setPending] = useState(false);
		const [lastUpdated, setLastUpdated] = useState<string | null>(null);
		const [checkInCount, setCheckInCount] = useState(0);
		const [showAllNegative, setShowAllNegative] = useState(false);
		const [showAllPositive, setShowAllPositive] = useState(false);
		const [pollingIntervalId, setPollingIntervalId] = useState<number | null>(
			null,
		);
		const [suggestIdx, setSuggestIdx] = useState(0);

		// Check if we're on an appropriate page for this component
		const isOnAppropriatePage =
			typeof window !== "undefined" &&
			(window.location.pathname === "/" ||
				window.location.pathname === "/home" ||
				window.location.pathname === "" ||
				window.location.pathname === "/daily-checkin" ||
				window.location.pathname === "/daily-checkin/complete");

		// Update analysis when initialAnalysis prop changes
		useEffect(() => {
			if (initialAnalysis) {
				setAnalysis(initialAnalysis);
				setLoading(false);
				// Set a recent timestamp since we just generated it
				setLastUpdated(new Date().toISOString());
			}
		}, [initialAnalysis]);

		useImperativeHandle(ref, () => ({
			refresh: fetchAnalysis,
			showPending: () => {
				setPending(true);
				startPolling();
			},
			getAnalysis: () => analysis,
			getState: () => ({
				loading,
				pending,
				hasAnalysis: !!analysis,
				lastUpdated,
			}),
		}));

		// Check if suggested reflection is similar to existing active reflections
		const isSuggestedReflectionSimilar = (suggestedTitle: string): boolean => {
			if (!suggestedTitle || activeReflections.length === 0) return false;

			const suggestedWords = suggestedTitle.toLowerCase().split(/\\s+/);
			const significantWords = suggestedWords.filter(
				(word) =>
					word.length > 3 &&
					![
						"with",
						"and",
						"the",
						"for",
						"from",
						"about",
						"that",
						"this",
					].includes(word),
			);

			return activeReflections.some((reflection) => {
				const existingWords = reflection.title.toLowerCase().split(/\\s+/);
				const matchingWords = significantWords.filter((word) =>
					existingWords.some(
						(existingWord) =>
							existingWord.includes(word) || word.includes(existingWord),
					),
				);

				// Consider similar if more than 30% of significant words match
				return (
					matchingWords.length / Math.max(significantWords.length, 1) > 0.3
				);
			});
		};

		useEffect(() => {
			if (user && isOnAppropriatePage) {
				fetchAnalysis();
			} else if (!isOnAppropriatePage) {
				setLoading(false);
				// Don't clear if we have initialAnalysis from props
				if (!initialAnalysis) {
					setAnalysis(null);
				}
			}

			// Cleanup polling on unmount
			return () => {
				if (pollingIntervalId) {
					clearInterval(pollingIntervalId);
				}
			};
		}, [user, isOnAppropriatePage]);

		const startPolling = () => {
			// Clear any existing polling
			if (pollingIntervalId) {
				clearInterval(pollingIntervalId);
			}

			// Poll every 5 seconds for new analysis
			const intervalId = window.setInterval(async () => {
				const newAnalysisFound = await checkForNewAnalysis();
				if (newAnalysisFound) {
					setPending(false);
					clearInterval(intervalId);
					setPollingIntervalId(null);
				}
			}, 5000);

			setPollingIntervalId(Number(intervalId));

			// Stop polling after 2 minutes to prevent infinite polling
			setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
					setPollingIntervalId(null);
					setPending(false);
				}
			}, 120000); // 2 minutes
		};

		const checkForNewAnalysis = async (): Promise<boolean> => {
			if (!user) return false;

			try {
				// Fixed date calculation with validation
				const now = new Date();
				const currentYear = now.getFullYear();

				// Validate that we're in a reasonable year range
				if (currentYear < 2020 || currentYear > 2030) {
					return false;
				}

				const today = now.toISOString().split("T")[0];

				// Get current count of check-ins today
				const { data: todayCheckIns, error: checkInError } = await supabase
					.from("daily_checkins")
					.select("id")
					.eq("user_id", user.id)
					.gte("created_at", today + "T00:00:00.000Z")
					.lt("created_at", today + "T23:59:59.999Z");

				if (checkInError) {
					return false;
				}

				const currentCheckInCount = todayCheckIns?.length || 0;

				// Fetch today's analysis
				const { data: analysisData, error } = await supabase
					.from("daily_checkin_analysis")
					.select("*")
					.eq("user_id", user.id)
					.eq("analysis_date", today)
					.maybeSingle(); // Use maybeSingle() to avoid 406 errors

				if (error as any) {
					// Don't continue polling if there's a database access issue
					if (
						(error as any)?.code === "42P01" ||
						(error as any)?.message?.includes("does not exist") ||
						(error as any)?.message?.includes("406")
					) {
					}
					return false;
				}

				if (analysisData && analysisData.checkin_count >= currentCheckInCount) {
					setAnalysis(analysisData.analysis_data);
					setLastUpdated(analysisData.updated_at);
					setCheckInCount(analysisData.checkin_count ?? 0);
					return true;
				}

				return false;
			} catch (error) {
				return false;
			}
		};

		const fetchAnalysis = async () => {
			if (!user) {
				return;
			}

			if (!isOnAppropriatePage) {
				setLoading(false);
				return;
			}

			setLoading(true);
			try {
				// Fixed date calculation with validation
				const now = new Date();
				const currentYear = now.getFullYear();

				// Validate that we're in a reasonable year range
				if (currentYear < 2020 || currentYear > 2030) {
					// Use a fallback date or skip the query
					// Don't clear if we have initialAnalysis from props
					if (!initialAnalysis) {
						setAnalysis(null);
						setLastUpdated(null);
						setCheckInCount(0);
					}
					setLoading(false);
					return;
				}

				const today = now.toISOString().split("T")[0];

				// Fetch today's analysis

				let analysisData = null;
				let error = null;

				try {
					const result = await supabase
						.from("daily_checkin_analysis")
						.select("*")
						.eq("user_id", user.id)
						.eq("analysis_date", today)
						.maybeSingle(); // Use maybeSingle() to avoid 406 errors

					analysisData = result.data;
					error = result.error;
				} catch (fetchError: any) {
					error = fetchError;
					// Handle specific 406 errors
					if (
						(fetchError as any)?.message?.includes("406") ||
						(fetchError as any)?.message?.includes("Not Acceptable")
					) {
						// Don't clear if we have initialAnalysis from props
						if (!initialAnalysis) {
							setAnalysis(null);
							setLastUpdated(null);
							setCheckInCount(0);
						}
						setLoading(false);
						return;
					}
				}

				// Also check if there are ANY analysis records for this user (debugging)
				const { data: allUserAnalysis, error: allError } = await supabase
					.from("daily_checkin_analysis")
					.select("analysis_date, checkin_count, created_at")
					.eq("user_id", user.id)
					.order("analysis_date", { ascending: false })
					.limit(5);

				if (error as any) {
					// Check if it's a table access issue (406, 42P01, etc.)
					if (
						(error as any)?.code === "42P01" ||
						(error as any)?.message?.includes("does not exist") ||
						(error as any)?.message?.includes("406")
					) {
					}
					// Don't clear if we have initialAnalysis from props
					if (!initialAnalysis) {
						setAnalysis(null);
						setLastUpdated(null);
						setCheckInCount(0);
					}
					return;
				}

				if (analysisData) {
					// Ensure arrays exist with defaults to prevent undefined errors
					const rawData = analysisData.analysis_data || {};
					const safeAnalysisData = {
						mainTopics: Array.isArray(rawData.mainTopics)
							? rawData.mainTopics
							: [],
						negativeTopics: Array.isArray(rawData.negativeTopics)
							? rawData.negativeTopics
							: [],
						positiveTopics: Array.isArray(rawData.positiveTopics)
							? rawData.positiveTopics
							: [],
						reflectionSuggestion: rawData.reflectionSuggestion || null,
						reflectionSuggestions: Array.isArray(rawData.reflectionSuggestions)
							? (
									rawData.reflectionSuggestions as Array<{
										title: string;
										description: string;
										reasoning?: string;
									}>
								).slice(0, 3)
							: Array.isArray(rawData.suggestedReflections)
								? (
										rawData.suggestedReflections as Array<{
											title: string;
											description: string;
											reasoning?: string;
										}>
									).slice(0, 3)
								: undefined,
					};

					setAnalysis(safeAnalysisData);
					setLastUpdated(analysisData.updated_at);
					setCheckInCount(analysisData.checkin_count ?? 0);
				} else {
					// If no analysis for today, look for the most recent analysis

					const { data: recentAnalysisResults, error: recentError } =
						await supabase
							.from("daily_checkin_analysis")
							.select("*")
							.eq("user_id", user.id)
							.order("analysis_date", { ascending: false })
							.limit(1);

					const recentAnalysisData =
						recentAnalysisResults && recentAnalysisResults.length > 0
							? recentAnalysisResults[0]
							: null;

					if (recentError) {
						// Don't clear if we have initialAnalysis from props
						if (!initialAnalysis) {
							setAnalysis(null);
							setLastUpdated(null);
							setCheckInCount(0);
						}
					} else if (recentAnalysisData) {
						// Use recent analysis data with same processing as today's data
						const rawData = recentAnalysisData.analysis_data || {};
						const safeAnalysisData = {
							mainTopics: Array.isArray(rawData.mainTopics)
								? rawData.mainTopics
								: [],
							negativeTopics: Array.isArray(rawData.negativeTopics)
								? rawData.negativeTopics
								: [],
							positiveTopics: Array.isArray(rawData.positiveTopics)
								? rawData.positiveTopics
								: [],
							reflectionSuggestion: rawData.reflectionSuggestion || null,
							reflectionSuggestions: Array.isArray(
								rawData.reflectionSuggestions,
							)
								? (
										rawData.reflectionSuggestions as Array<{
											title: string;
											description: string;
											reasoning?: string;
										}>
									).slice(0, 3)
								: Array.isArray(rawData.suggestedReflections)
									? (
											rawData.suggestedReflections as Array<{
												title: string;
												description: string;
												reasoning?: string;
											}>
										).slice(0, 3)
									: undefined,
						};

						setAnalysis(safeAnalysisData);
						setLastUpdated(recentAnalysisData.updated_at);
						setCheckInCount(recentAnalysisData.checkin_count ?? 0);
					} else {
						// Check if there are any check-ins today
						const { data: checkIns, error: checkInError } = await supabase
							.from("daily_checkins")
							.select("id, created_at")
							.eq("user_id", user.id)
							.gte("created_at", today + "T00:00:00.000Z")
							.lt("created_at", today + "T23:59:59.999Z");

						// Also check for any check-ins in the last 7 days
						const sevenDaysAgo = new Date();
						sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
						const { data: recentCheckIns } = await supabase
							.from("daily_checkins")
							.select("id, created_at")
							.eq("user_id", user.id)
							.gte("created_at", sevenDaysAgo.toISOString())
							.order("created_at", { ascending: false });

						// Don't clear if we have initialAnalysis from props
						if (!initialAnalysis) {
							setAnalysis(null);
							setLastUpdated(null);
							setCheckInCount(0);
						}
					}
				}
			} catch (error) {
			} finally {
				setLoading(false);
			}
		};

		const getEmotionColor = (emotion: string): string => {
			const colors: Record<string, string> = {
				Joy: "#FFD700",
				Trust: "#32CD32",
				Fear: "#800080",
				Surprise: "#FF8C00",
				Sadness: "#4169E1",
				Disgust: "#228B22",
				Anger: "#FF0000",
				Anticipation: "#FFA500",
			};
			return colors[emotion] || "#6b7280";
		};

		if (loading) {
			return (
				<Card>
					<CardHeader>
						<div className="flex items-center space-x-2">
							<Brain className="h-5 w-5" />
							<CardTitle>Daily Check-in Analysis</CardTitle>
						</div>
						<CardDescription>
							AI-powered insights from your check-ins
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex items-center justify-center h-32">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
						</div>
					</CardContent>
				</Card>
			);
		}

		if (pending) {
			return (
				<Card>
					<CardHeader>
						<div className="flex items-center space-x-2">
							<Brain className="h-5 w-5" />
							<CardTitle>Daily Check-in Analysis</CardTitle>
						</div>
						<CardDescription>
							Generating fresh insights from your latest check-in...
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="flex flex-col items-center justify-center h-32 space-y-4">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
							<div className="text-center">
								<p className="text-sm text-muted-foreground">
									Analyzing your recent check-ins
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									This usually takes 30-60 seconds
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			);
		}

		if (!analysis) {
			return (
				<div className="space-y-6">
					<Card>
						<CardHeader>
							<div className="flex items-center space-x-2">
								<Brain className="h-5 w-5" />
								<CardTitle>Daily Check-in Analysis</CardTitle>
							</div>
							<CardDescription>
								AI-powered insights from your check-ins
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="text-center py-8">
								<AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
								<h4 className="text-lg font-medium text-foreground mb-2">
									No Analysis Available
								</h4>
								<p className="text-muted-foreground mb-4">
									Generate AI-powered insights from your recent check-ins to see
									patterns and receive personalized recommendations.
								</p>
								<div className="space-y-3">
									<div className="text-xs text-muted-foreground">
										Analysis updated: {new Date().toISOString().split("T")[0]}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Manual Analysis Generation */}
					<AnalysisGenerator onAnalysisGenerated={fetchAnalysis} />
				</div>
			);
		}

		// Debug logging

		return (
			<div className="space-y-6">
				{/* Header */}
				<div className={`mb-6 ${centerHeader ? "text-center" : ""}`}>
					<div
						className={`flex items-center ${centerHeader ? "justify-center" : "justify-between"} mb-2`}
					>
						<div className="flex items-center space-x-2">
							<Brain className="h-5 w-5" />
							<h2 className="text-2xl font-semibold leading-none tracking-tight">
								Daily Check-in Analysis
							</h2>
						</div>
						{!centerHeader && (
							<div className="flex items-center space-x-2"></div>
						)}
					</div>
					<p
						className={`text-sm text-muted-foreground ${centerHeader ? "text-center" : ""}`}
					>
						AI-powered insights from your last {checkInCount} check-ins
						{lastUpdated && (
							<span className="ml-2 text-muted-foreground text-sm">
								• Last analyzed{" "}
								{formatDistanceToNow(new Date(lastUpdated), {
									addSuffix: true,
								})}
								{(() => {
									const analysisDate = lastUpdated
										? new Date(lastUpdated).toISOString().split("T")[0]
										: null;
									const today = new Date().toISOString().split("T")[0];
									return analysisDate && analysisDate !== today ? (
										<span className="text-amber-600 dark:text-amber-400">
											{" "}
											(showing recent analysis)
										</span>
									) : null;
								})()}
							</span>
						)}
					</p>
				</div>

				{/* Analysis Content */}
				{analysis && (
					<>
						{/* Show message if analysis exists but has no meaningful data */}
						{(!analysis.mainTopics || analysis.mainTopics.length === 0) &&
							(!analysis.negativeTopics ||
								analysis.negativeTopics.length === 0) &&
							(!analysis.positiveTopics ||
								analysis.positiveTopics.length === 0) &&
							!analysis.reflectionSuggestion &&
							!(analysis as any).reflectionSuggestions && (
								<div className="space-y-6">
									<Card>
										<CardContent className="text-center py-8">
											<AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
											<h4 className="text-lg font-medium text-foreground mb-2">
												Incomplete Analysis Data
											</h4>
											<p className="text-muted-foreground mb-4">
												The analysis exists but seems to be missing data. Try
												generating a fresh analysis below.
											</p>
										</CardContent>
									</Card>

									{/* Manual Analysis Generation for incomplete data */}
									<AnalysisGenerator onAnalysisGenerated={fetchAnalysis} />
								</div>
							)}

						{/* Negative and Positive Topics */}
						<div
							className={`grid gap-6 ${
								analysis.negativeTopics?.length > 0 &&
								analysis.positiveTopics?.length > 0
									? "grid-cols-1 md:grid-cols-2"
									: "grid-cols-1"
							}`}
						>
							{/* Negative Topics */}
							{analysis.negativeTopics &&
								analysis.negativeTopics.length > 0 && (
									<Card>
										<CardHeader>
											<div className="flex items-center space-x-2">
												<TrendingDown className="h-5 w-5 text-red-500" />
												<CardTitle className="text-base">
													Areas of Concern
												</CardTitle>
											</div>
											<CardDescription>
												Topics that may need attention
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="space-y-3">
												{analysis.negativeTopics
													?.sort((a, b) => a.sentiment - b.sentiment)
													?.slice(0, showAllNegative ? undefined : 2)
													?.map((topic, index) => (
														<div
															key={index}
															className="p-4 rounded-lg border border-red-100 bg-red-50 dark:border-red-900 dark:bg-red-950"
														>
															<div className="mb-3">
																<div className="flex-1">
																	<div className="font-medium text-base mb-2">
																		{topic.topic}
																	</div>
																	<div className="text-xs text-muted-foreground mb-2">
																		{topic.frequency} mentions
																	</div>
																	<div className="space-y-2">
																		<div>
																			<span className="text-xs font-medium text-red-700 dark:text-red-400">
																				Impact:
																			</span>
																			<p className="text-sm text-muted-foreground mt-1">
																				{topic.impact}
																			</p>
																		</div>
																		<div>
																			<span className="text-xs font-medium text-red-700 dark:text-red-400">
																				Patterns:
																			</span>
																			<p className="text-sm text-muted-foreground mt-1">
																				{topic.patterns}
																			</p>
																		</div>
																		{topic.trends && (
																			<div>
																				<span className="text-xs font-medium text-red-700 dark:text-red-400">
																					Trends:
																				</span>
																				<p className="text-sm text-muted-foreground mt-1">
																					{topic.trends}
																				</p>
																			</div>
																		)}
																		{topic.specifics && (
																			<div>
																				<span className="text-xs font-medium text-red-700 dark:text-red-400">
																					Specifics:
																				</span>
																				<p className="text-sm text-muted-foreground mt-1">
																					{topic.specifics}
																				</p>
																			</div>
																		)}
																	</div>
																</div>
															</div>
														</div>
													))}
												{analysis.negativeTopics &&
													analysis.negativeTopics.length > 2 && (
														<button
															onClick={() =>
																setShowAllNegative(!showAllNegative)
															}
															className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
														>
															{showAllNegative
																? "View less"
																: `View more (${(analysis.negativeTopics?.length || 0) - 2} more)`}
														</button>
													)}
											</div>
										</CardContent>
									</Card>
								)}

							{/* Positive Topics */}
							{analysis.positiveTopics &&
								analysis.positiveTopics.length > 0 && (
									<Card>
										<CardHeader>
											<div className="flex items-center space-x-2">
												<TrendingUp className="h-5 w-5 text-green-500" />
												<CardTitle className="text-base">
													Strengths & Positives
												</CardTitle>
											</div>
											<CardDescription>
												Things that are going well
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="space-y-3">
												{analysis.positiveTopics
													?.sort((a, b) => b.sentiment - a.sentiment)
													?.slice(0, showAllPositive ? undefined : 2)
													?.map((topic, index) => (
														<div
															key={index}
															className="p-4 rounded-lg border border-green-100 bg-green-50 dark:border-green-900 dark:bg-green-950"
														>
															<div className="mb-3">
																<div className="flex-1">
																	<div className="font-medium text-base mb-2">
																		{topic.topic}
																	</div>
																	<div className="text-xs text-muted-foreground mb-2">
																		{topic.frequency} mentions
																	</div>
																	<div className="space-y-2">
																		<div>
																			<span className="text-xs font-medium text-green-700 dark:text-green-400">
																				Impact:
																			</span>
																			<p className="text-sm text-muted-foreground mt-1">
																				{topic.impact}
																			</p>
																		</div>
																		<div>
																			<span className="text-xs font-medium text-green-700 dark:text-green-400">
																				Patterns:
																			</span>
																			<p className="text-sm text-muted-foreground mt-1">
																				{topic.patterns}
																			</p>
																		</div>
																		{topic.trends && (
																			<div>
																				<span className="text-xs font-medium text-green-700 dark:text-green-400">
																					Trends:
																				</span>
																				<p className="text-sm text-muted-foreground mt-1">
																					{topic.trends}
																				</p>
																			</div>
																		)}
																		{topic.specifics && (
																			<div>
																				<span className="text-xs font-medium text-green-700 dark:text-green-400">
																					Specifics:
																				</span>
																				<p className="text-sm text-muted-foreground mt-1">
																					{topic.specifics}
																				</p>
																			</div>
																		)}
																	</div>
																</div>
															</div>
														</div>
													))}
												{analysis.positiveTopics &&
													analysis.positiveTopics.length > 2 && (
														<button
															onClick={() =>
																setShowAllPositive(!showAllPositive)
															}
															className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
														>
															{showAllPositive
																? "View less"
																: `View more (${(analysis.positiveTopics?.length || 0) - 2} more)`}
														</button>
													)}
											</div>
										</CardContent>
									</Card>
								)}
						</div>
					</>
				)}
			</div>
		);
	},
);

DailyCheckInAnalysisClean.displayName = "DailyCheckInAnalysisClean";
